import apiClient from './client';
import { apiFailure } from './errors';
import type {
  WKLoginRequest,
  WKLoginResponse,
  LinkWkResponse,
  EmailCodeResponse,
  EmailLoginResponse,
  GoogleLoginResponse,
  AccountStatus,
} from '@/types/auth';

interface SessionResponse {
  status: string;
  data: {
    user: {
      wkIdentity?: string;
      enterpriseEmail?: string;
      displayName?: string;
      accountStatus: AccountStatus;
    };
    expiresAt: string;
  };
}

interface LoginApiResponse {
  status: string;
  data: {
    sessionToken: string;
    expiresAt: string;
    user: {
      wkIdentity?: string;
      enterpriseEmail?: string;
      displayName?: string;
      accountStatus: AccountStatus;
    };
  };
}

export const authApi = {
  // WK Identity login
  loginWithWK: async (data: WKLoginRequest): Promise<WKLoginResponse> => {
    const response = await apiClient.post<LoginApiResponse>('/auth/wk', data);
    const result = response.data.data as LoginApiResponse['data'] & {
      success?: boolean;
      error?: string;
    };
    if (result.success === false) {
      throw apiFailure(result, 'Authentication failed');
    }
    return {
      sessionToken: result.sessionToken,
      expiresAt: result.expiresAt,
      user: result.user,
    };
  },

  // Validate current session
  getSession: async () => {
    const response = await apiClient.get<SessionResponse>('/auth/session');
    const result = response.data.data as SessionResponse['data'] & { valid?: boolean };
    if (result.valid === false) {
      throw new Error('Session is not valid');
    }
    return result;
  },

  // Logout
  logout: async (): Promise<void> => {
    await apiClient.post('/auth/logout');
  },

  // Email login - request code
  requestEmailLoginCode: async (email: string): Promise<EmailCodeResponse> => {
    const response = await apiClient.post<{ status: string; data: EmailCodeResponse }>(
      '/auth/email/request',
      { email }
    );
    return response.data.data;
  },

  // Email login - verify code
  loginWithEmailCode: async (email: string, code: string): Promise<EmailLoginResponse> => {
    const response = await apiClient.post<{
      status: string;
      data: {
        success: boolean;
        sessionToken: string;
        expiresAt: string;
        user: {
          wkIdentity?: string;
          enterpriseEmail?: string;
          displayName?: string;
          accountStatus: AccountStatus;
        };
        error?: string;
      };
    }>('/auth/email/verify', { email, code });

    if (!response.data.data.success) {
      throw apiFailure(response.data.data, 'Login failed');
    }

    return {
      sessionToken: response.data.data.sessionToken,
      expiresAt: response.data.data.expiresAt,
      user: response.data.data.user,
    };
  },

  // Link WK Identity to email-based account
  linkWkIdentity: async (data: WKLoginRequest): Promise<LinkWkResponse> => {
    const response = await apiClient.post<{
      status: string;
      data: {
        success: boolean;
        user?: {
          wkIdentity: string;
          enterpriseEmail?: string;
          displayName?: string;
          accountStatus: AccountStatus;
        };
        error?: string;
      };
    }>('/auth/link-wk', data);

    if (!response.data.data.success || !response.data.data.user) {
      throw apiFailure(response.data.data, 'Failed to link wallet');
    }

    return {
      user: response.data.data.user,
    };
  },

  // Google login (Authorization Code flow)
  loginWithGoogle: async (code: string, redirectUri: string): Promise<GoogleLoginResponse> => {
    const response = await apiClient.post<{
      status: string;
      data: {
        success: boolean;
        sessionToken: string;
        expiresAt: string;
        user: {
          wkIdentity?: string;
          enterpriseEmail?: string;
          displayName?: string;
          accountStatus: AccountStatus;
        };
        error?: string;
      };
    }>('/auth/google', { code, redirectUri });

    if (!response.data.data.success) {
      throw apiFailure(response.data.data, 'Login failed');
    }

    return {
      sessionToken: response.data.data.sessionToken,
      expiresAt: response.data.data.expiresAt,
      user: response.data.data.user,
    };
  },
};

export default authApi;
